import { useEffect, useRef, useState } from 'react';

import { isTransientGraphError } from '../graph/transientRetry';

// Shared helpers and lifecycle for the group-creation flows (standard ACL, confidential
// project, Role). The leaf helpers derive names and ride out Graph's replication lag;
// useGroupCreation owns the step state machine so each form only declares its steps.

// Derives a valid mailNickname from a displayName: replaces spaces and dots with
// hyphens, strips remaining special characters.
export const toMailNickname = (displayName: string): string =>
  displayName.replace(/[\s.]/g, '-').replace(/[^a-zA-Z0-9_-]/g, '');

// The charset allowed in the user-typed part of a group name (after the ACL_ / Role_
// prefix). Kept mailNickname-safe, since the create call derives the mailNickname from
// the name. Shared so the ACL and Role forms can't drift on what they accept. Forms
// warn + disable Create on a violation rather than silently stripping characters.
export const NAME_SUFFIX_PATTERN = /^[A-Za-z0-9_ -]*$/;
export const NAME_SUFFIX_HELP = 'Letters, numbers, spaces, hyphens and underscores only.';
export const NAME_SUFFIX_ERROR =
  'Only letters, numbers, spaces, hyphens and underscores are allowed.';

// Microsoft Graph is eventually consistent: a group created moments ago may not be
// referenceable yet, so nesting it can transiently fail. The error arrives as 404
// Request_ResourceNotFound, or — when the unreplicated object is the `$ref` member — as
// 400 Request_BadRequest with the same "does not exist / not present" message (#87), so
// the retry predicate matches on the message regardless of status (isTransientGraphError).
// Backoff runs longer than the submit path (which retries concurrent-modification, not a
// fresh-create) to give replication more room: ~1+2+4+8 = 15s total.
export const GRAPH_RETRY_DELAYS_MS = [1000, 2000, 4000, 8000];

// Reports an imminent backoff so a creation flow can show the user a live countdown
// (see RetryCountdown) instead of a spinner that looks hung. attempt/total are 1-based.
export type RetryInfo = { attempt: number; total: number; delayMs: number };

export const addWithRetry = async (
  add: () => Promise<unknown>,
  onRetry?: (info: RetryInfo) => void
): Promise<void> => {
  for (let attempt = 0; ; attempt++) {
    try {
      await add();
      return;
    } catch (e: any) {
      // Only transient replication/throttling failures are worth retrying — surface
      // anything else (auth, validation, …) at once.
      if (attempt >= GRAPH_RETRY_DELAYS_MS.length || !isTransientGraphError(e)) throw e;
      const delayMs = GRAPH_RETRY_DELAYS_MS[attempt];
      onRetry?.({ attempt: attempt + 1, delayMs, total: GRAPH_RETRY_DELAYS_MS.length });
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }
  }
};

// Pulls the human-readable message out of an RTK Query / Graph error, falling back
// to a step-specific default when the shape isn't what we expect.
export const graphErrorMessage = (e: any, fallback: string): string =>
  e?.data?.error?.message ?? fallback;

export type StepStatus = 'idle' | 'pending' | 'success' | 'error';

export type Step = {
  label: string;
  status: StepStatus;
  error?: string;
  // Present while a pending step is between retry attempts, so the UI can show a live
  // backoff countdown. Cleared once the step settles or its next attempt begins.
  retry?: RetryInfo;
};

// Passed to a step's run() so it can report progress back to the state machine — today
// just the retry hook to forward into addWithRetry for the countdown.
export type StepReporter = { onRetry: (info: RetryInfo) => void };

// One step in a creation run: a label, the async work, and the message to show if that
// work throws something without a Graph error message. run() may ignore the reporter
// (most steps don't retry) or forward reporter.onRetry into addWithRetry.
export type CreationStep = {
  label: string;
  errorFallback: string;
  run: (reporter: StepReporter) => Promise<void>;
};

// After all steps succeed we pause before firing onComplete, so the user can read the
// result and Graph gets a head start on replicating the new group(s). (getGroupById
// also retries the read-your-write 404, so the detail page self-heals if replication
// outlasts this pause — see ADR-0003.)
const NAVIGATE_DELAY_MS = 4000;

// Owns the create-flow lifecycle shared by every creation form: the ordered step list
// and its idle→pending→success/error status, the running/done/failed flags, and the
// post-success navigate timer (cleared on unmount so a late navigate can't fire after
// the form has gone away). A form supplies its steps and what to do once they succeed.
export function useGroupCreation() {
  const [steps, setSteps] = useState<Step[]>([]);
  const [isRunning, setIsRunning] = useState(false);
  const [done, setDone] = useState(false);
  const [failed, setFailed] = useState(false);

  const navTimerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Guards against a re-entrant execute() — e.g. a fast double-click firing onClick twice
  // before React re-renders and hides the confirm button. A ref (not isRunning state)
  // because state updates lag the synchronous second call.
  const runningRef = useRef(false);
  useEffect(() => () => clearTimeout(navTimerRef.current), []);

  const updateStep = (index: number, status: StepStatus, error?: string, retry?: RetryInfo) => {
    setSteps((prev) =>
      prev.map((step, i) => (i === index ? { ...step, error, retry, status } : step))
    );
  };

  // Run the steps in order, halting on the first failure. On success, pause then fire
  // onComplete (typically: close the modal and navigate to the new group).
  const execute = async (creationSteps: CreationStep[], onComplete: () => void) => {
    if (runningRef.current) return;
    runningRef.current = true;
    clearTimeout(navTimerRef.current);
    setIsRunning(true);
    setFailed(false);
    setDone(false);
    setSteps(creationSteps.map((s) => ({ label: s.label, status: 'idle' })));

    for (let i = 0; i < creationSteps.length; i++) {
      updateStep(i, 'pending');
      try {
        // A retry surfaces its countdown via step.retry; the next attempt (or success)
        // clears it by overwriting the step without a retry field.
        await creationSteps[i].run({
          onRetry: (info) => updateStep(i, 'pending', undefined, info),
        });
        updateStep(i, 'success');
      } catch (e) {
        updateStep(i, 'error', graphErrorMessage(e, creationSteps[i].errorFallback));
        setFailed(true);
        setIsRunning(false);
        runningRef.current = false;
        return;
      }
    }

    setIsRunning(false);
    setDone(true);
    navTimerRef.current = setTimeout(onComplete, NAVIGATE_DELAY_MS);
  };

  return { done, execute, failed, isRunning, steps };
}
